import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Modal, Field, Badge, Empty } from "@/components/Kit";
import { Plus, Pencil, Trash2, Users } from "lucide-react";

const EMPTY = { name: "", sections: "A", class_teacher: "", order: 0 };

export default function ClassesTab() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const { data: classes = [] } = useQuery({ queryKey: ["classes"], queryFn: async () => (await api.get("/school/academics/classes")).data });
  const { data: students = [] } = useQuery({ queryKey: ["students"], queryFn: async () => (await api.get("/school/students")).data });

  const save = useMutation({
    mutationFn: async () => {
      const body = { ...form, sections: form.sections.split(",").map((s) => s.trim()).filter(Boolean), order: Number(form.order) || 0 };
      return editing ? (await api.put(`/school/academics/classes/${editing.id}`, body)).data : (await api.post("/school/academics/classes", body)).data;
    },
    onSuccess: () => { toast.success(editing ? "Class updated" : "Class created"); setOpen(false); qc.invalidateQueries({ queryKey: ["classes"] }); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  const remove = useMutation({
    mutationFn: async (id) => (await api.delete(`/school/academics/classes/${id}`)).data,
    onSuccess: () => { toast.success("Class removed"); qc.invalidateQueries({ queryKey: ["classes"] }); },
  });

  const openNew = () => { setEditing(null); setForm({ ...EMPTY, order: classes.length }); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ name: c.name, sections: c.sections.join(", "), class_teacher: c.class_teacher || "", order: c.order }); setOpen(true); };
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const count = (name, sec) => students.filter((s) => s.class_name === name && (!sec || s.section === sec)).length;

  return (
    <div data-testid="classes-tab">
      <div className="mb-4 flex justify-end"><button className="ep-btn" onClick={openNew} data-testid="add-class-btn"><Plus size={16} /> Add Class</button></div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {classes.map((c) => (
          <div key={c.id} className="ep-card p-5" data-testid={`class-card-${c.name}`}>
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Class</p>
                <h3 className="text-2xl font-bold font-head text-white">{c.name}</h3>
              </div>
              <div className="flex gap-1">
                <button onClick={() => openEdit(c)} className="rounded-lg bg-slate-700/40 p-2 text-slate-300 hover:bg-slate-700/70" data-testid={`edit-class-${c.name}`}><Pencil size={14} /></button>
                <button onClick={() => remove.mutate(c.id)} className="rounded-lg bg-rose-500/15 p-2 text-rose-400 hover:bg-rose-500/25" data-testid={`del-class-${c.name}`}><Trash2 size={14} /></button>
              </div>
            </div>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {c.sections.map((s) => <Badge key={s} color="#06B6D4">Sec {s} · {count(c.name, s)}</Badge>)}
            </div>
            <div className="mt-4 flex items-center justify-between text-xs text-slate-500">
              <span>{c.class_teacher ? `Teacher: ${c.class_teacher}` : "No class teacher"}</span>
              <span className="flex items-center gap-1"><Users size={12} /> {count(c.name)} students</span>
            </div>
          </div>
        ))}
        {!classes.length && <div className="col-span-full"><Empty>No classes yet. Add your first class.</Empty></div>}
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Edit Class" : "Add Class"} testid="class-modal">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Class Name *" value={form.name} onChange={upd("name")} placeholder="e.g. 5 / UKG" data-testid="cls-name" />
          <Field label="Display Order" type="number" value={form.order} onChange={upd("order")} data-testid="cls-order" />
          <div className="col-span-2"><Field label="Sections (comma separated)" value={form.sections} onChange={upd("sections")} placeholder="A, B, C" data-testid="cls-sections" /></div>
          <div className="col-span-2"><Field label="Class Teacher" value={form.class_teacher} onChange={upd("class_teacher")} data-testid="cls-teacher" /></div>
        </div>
        <button className="ep-btn mt-5 w-full" disabled={save.isPending || !form.name} onClick={() => save.mutate()} data-testid="save-class-btn">{save.isPending ? "Saving…" : "Save Class"}</button>
      </Modal>
    </div>
  );
}
